/**
 * Build the `customer-label` print request for a work item.
 *
 * The Print page has its own builder for this label
 * (`buildPrintLabelRequest`'s `customer` branch) because it prints for a
 * customer picked out of the index. Here the customer is whoever the work item
 * belongs to, so the fields come off the item — but the request that reaches
 * `POST /api/print` has to be identical either way, since the same 90mm
 * template renders it. Kept as a pure function (and tested as one) for the same
 * reason the Print page's is: the validation rules are worth exercising without
 * rendering a panel.
 */
import type { PrintLabelRequest } from '@techgitter/contracts';
import { CUSTOMER_LABEL_DOCUMENT } from '../config/labelCatalog';
import { LABEL_LANE_COUNTER } from '../config/printLanes';

/** The work-item fields this label reads. */
export interface CustomerLabelWorkItem {
  customerName?: string | null;
  customerBusinessName?: string | null;
  customerMobile?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  /** Dataverse customer GUID — the QR fallback when there's no RS id. */
  customerId?: string | null;
}

export interface CustomerLabelOptions {
  /**
   * RepairShopr customer id, resolved from the customer index. Preferred over
   * the Dataverse GUID for the QR because `/customer/:id` resolves either and
   * the short numeric form scans off a 90mm label far more reliably.
   */
  rsCustomerId?: number;
  /** App origin the QR deep-links into, i.e. `window.location.origin`. */
  origin: string;
}

export type CustomerLabelResult =
  | { ok: true; request: PrintLabelRequest }
  | { ok: false; error: string };

export function buildWorkItemCustomerLabelRequest(
  workItem: CustomerLabelWorkItem,
  { rsCustomerId, origin }: CustomerLabelOptions,
): CustomerLabelResult {
  // Same precedence as the Print page: the business is who the machine belongs
  // to, so it leads when there is one.
  const customerName = (
    workItem.customerBusinessName || workItem.customerName || ''
  ).trim();
  if (!customerName) {
    return { ok: false, error: 'This work item has no customer to print.' };
  }

  const data: Record<string, string> = { customerName };

  const linkId = rsCustomerId || workItem.customerId || '';
  if (linkId) data.qrUrl = `${origin}/customer/${linkId}`;

  // Omit-empty, not blank-fill: the template renders only the fields it is
  // given, so an absent mobile collapses the line rather than printing a label
  // with a dangling "Mobile:".
  const mobile = (workItem.customerMobile || workItem.customerPhone || '').trim();
  if (mobile) data.mobile = mobile;

  const email = (workItem.customerEmail || '').trim();
  if (email) data.email = email;

  return {
    ok: true,
    request: {
      document: CUSTOMER_LABEL_DOCUMENT,
      data,
      media: '90mm',
      copies: 1,
      role: LABEL_LANE_COUNTER,
    },
  };
}
